// src/context/AuthContext.js
import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';
import authService from '../services/authService';
import { Loader2 } from 'lucide-react';

const AuthContext = createContext(undefined);

export const useAuthContext = () => {
    const context = useContext(AuthContext);
    if (context === undefined) {
        throw new Error('useAuthContext باید درون یک AuthProvider استفاده شود');
    }
    return context;
};

export function AuthProvider({ children }) {
    const [user, setUser] = useState(null);
    const [isLoggedIn, setIsLoggedIn] = useState(false);
    const [isModalOpen, setIsModalOpen] = useState(false);
    const [isLoading, setIsLoading] = useState(true);
    const [profileUpdateKey, setProfileUpdateKey] = useState(0);

    const triggerProfileUpdate = useCallback(() => {
        setProfileUpdateKey(prev => prev + 1);
    }, []);

    useEffect(() => {
        const initializeAuth = () => {
            try {
                const token = authService.getAuthTokens();
                const storedUser = authService.getUserInfo();
                if (token && storedUser) {
                    setIsLoggedIn(true);
                    setUser(storedUser);
                } else {
                    authService.clearAuthData();
                    setIsLoggedIn(false);
                    setUser(null);
                }
            } catch {
                authService.clearAuthData();
            } finally {
                setIsLoading(false);
            }
        };
        initializeAuth();
    }, []);

    const closeAuthModal = useCallback(() => setIsModalOpen(false), []);
    const openAuthModal = useCallback(() => {
        if (!isLoggedIn) setIsModalOpen(true);
    }, [isLoggedIn]);

    const login = useCallback(async (identifier, password, rememberMe = false) => {
        const userInfo = await authService.loginUser(identifier, password, rememberMe);
        setUser(userInfo);
        setIsLoggedIn(true);
        triggerProfileUpdate();
        closeAuthModal();
        return userInfo;
    }, [closeAuthModal, triggerProfileUpdate]);

    const register = useCallback(async (phone, email, password, fullName) => {
        const response = await authService.registerUser(phone, email, password, fullName);
        return response;
    }, []);

    const loginWithToken = useCallback((accessToken, refreshToken, userInfo) => {
        authService.setAuthData({
            token: accessToken,
            refresh_token: refreshToken,
            user_info: userInfo,
            remember_me: false,
        });
        setUser(userInfo);
        setIsLoggedIn(true);
        triggerProfileUpdate();
        closeAuthModal();
    }, [closeAuthModal, triggerProfileUpdate]);

    const handleLogout = useCallback(() => {
        authService.logout();
        setIsLoggedIn(false);
        setUser(null);
        triggerProfileUpdate();
    }, [triggerProfileUpdate]);

    const value = {
        user,
        isLoggedIn,
        isModalOpen,
        isLoading,
        openAuthModal,
        closeAuthModal,
        login,
        register,
        loginWithToken,
        handleLogout,
        profileUpdateKey,
        triggerProfileUpdate,
        getAuthTokens: authService.getAuthTokens,
    };

    if (isLoading) {
        return (
            <div className="fixed inset-0 flex flex-col items-center justify-center bg-white z-[100]">
                <Loader2 className="w-12 h-12 animate-spin text-purple-700 mb-4" />
                <span className="text-gray-600 font-medium">در حال بررسی دسترسی...</span>
            </div>
        );
    }

    return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}